<template>
    <div class="settings-container">
        <!-- Section 0: User Account (Hidden in Web Version) -->
        <div v-if="!isWeb" class="user-section section-box">
            <h3>👤 帳戶 <span class="beta-tag">Beta</span></h3>
            <div v-if="state.isLoading" class="loading-state">載入中...</div>
            <div v-else-if="state.isSignedIn" class="user-info">
                <div class="user-details">
                    <p class="user-email">{{ displayEmail }}</p>
                    <p class="sync-status">✅ 雲端同步已開啟</p>
                </div>
                <button @click="handleSignOut" class="sign-out-btn">登出</button>
            </div>
            <div v-else class="auth-options">
                <p>登入後可享受雲端同步及建立多個規則的功能。</p>
                <div class="auth-btns">
                    <button @click="handleGoogleSignIn" class="google-btn">
                        <svg class="icon" viewBox="0 0 24 24" width="18" height="18">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Google 登入
                    </button>
                    <button v-if="!isAndroid" @click="handleAppleSignIn" class="apple-btn">
                        <svg class="icon" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                            <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                        </svg>
                        Apple 登入
                    </button>
                </div>
            </div>
        </div>

        <!-- Section 1: Game Profile Management (Import/Export/Save/Delete) -->
        <div v-if="!isWeb" class="profile-section section-box">
            <h3>🗂️ 牌局規則管理</h3>
            
            <!-- Vertical Profile List -->
            <div class="profile-list">
                <div 
                    v-for="profile in sortedProfiles" 
                    :key="profile.id" 
                    :class="['profile-item', { active: state.activeProfileId === profile.id }]"
                    @click="handleProfileSwitch(profile.id)"
                >
                    <div class="profile-info">
                        <span class="active-indicator" v-if="state.activeProfileId === profile.id">✓</span>
                        <div class="name-date-stack">
                            <span class="profile-name-text">{{ profile.name }}</span>
                            <span class="profile-date-text" v-if="profile.updatedAt">
                                最後更新: {{ formatDate(profile.updatedAt) }}
                            </span>
                        </div>
                    </div>
                    <div class="profile-item-actions">
                        <div v-if="state.activeProfileId === profile.id && renamingProfileId === profile.id" class="inline-rename-wrap" @click.stop>
                            <input 
                                v-model="renamingValue" 
                                class="mini-rename-input"
                                @keyup.enter="confirmRename(profile)"
                                @keyup.esc="cancelRename"
                                @blur="confirmRename(profile)"
                                ref="renameInput"
                            />
                        </div>
                        <template v-else>
                            <button 
                                v-if="!profile.isDefault && state.isSignedIn" 
                                class="mini-btn edit" 
                                @click.stop="startRename(profile)" 
                                title="重新命名"
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                </svg>
                            </button>
                            <button 
                                v-if="!profile.isDefault"
                                class="mini-btn share" 
                                @click.stop="exportProfile(profile)" 
                                title="分享連結"
                                :disabled="isSharingProfileId === profile.id"
                            >
                                <span v-if="isSharingProfileId === profile.id" class="share-spinner"></span>
                                <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                    <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                                </svg>
                            </button>
                            <button 
                                v-if="!profile.isDefault" 
                                class="mini-btn delete" 
                                @click.stop="deleteProfileById(profile)" 
                                title="刪除"
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                </svg>
                            </button>
                        </template>
                    </div>
                </div>
            </div>

            <div class="profile-lower-row">
                <!-- Detailed rename input removed as it's now inline in the list above -->
                <div class="footer-btns">
                    <button class="create-new-btn" @click="handleCreateBtnClick" :disabled="isWeb">+ 新增規則</button>
                    <button class="import-toggle-btn" @click="handleImportBtnClick" :disabled="isWeb">或貼上連結</button>
                </div>
                <p v-if="!state.isSignedIn && !isWeb" class="login-nudge">💡 登入後即可建立多個規則</p>
                <p v-else-if="!isWeb" class="login-nudge">💡 朋友開啟你分享的連結即可匯入；下方可手動貼上連結</p>
            </div>

            <!-- Inline Import Area -->
            <div v-if="showImportArea" class="import-sub-section">
                <p class="import-hint">或貼上分享連結匯入</p>
                <textarea v-model="importText" :placeholder="`例如: ${APP_WEB_ORIGIN}/share/…`" class="import-textarea"></textarea>
                <div class="import-btns">
                    <button @click="handleImport" class="confirm-import-btn" :disabled="isImporting">
                        <span v-if="isImporting" class="share-spinner"></span>
                        <span v-else>確認匯入</span>
                    </button>
                    <button @click="showImportArea = false" class="cancel-btn-small">取消</button>
                </div>
            </div>

            <div v-if="state.activeProfileId === 'default' && hasChangesSinceLoad && state.isSignedIn" class="default-nudge">
                💡 你正在修改「標準規則」。建議建立一個新規則來儲存。
                <button @click="showCreateDialog = true" class="nudge-link">立即建立</button>
            </div>
        </div>

        <!-- Section 2: Add New Rules Section (Original Part) -->
        <div class="add-score-section section-box">
            <h3>➕ 在「{{ state.activeProfile.name }}」新增規則</h3>
            <div class="form-row">
                <input v-model="newOptionName" placeholder="名稱 (例如: 八台花)" class="input-name" />
                <input v-model.number="newOptionValue" type="number" placeholder="番數" class="input-value" @keypress="onlyNumber" />
                <select v-model="selectedGroupIndex" class="select-group">
                    <option v-for="(group, index) in groups" :key="index" :value="index">
                        {{ group.name }}
                    </option>
                </select>
                <button @click="addNewScore" class="add-btn">新增</button>
            </div>
            <p v-if="errorMessage" class="error-msg">{{ errorMessage }}</p>
        </div>

        <!-- Section 3: List of All Scores -->
        <div v-for="(group, gIndex) in groups" :key="group.name" class="group-section">
            <div class="group-header">
                <h2>{{ group.name }}</h2>
                <span class="count-tag">{{ group.options.length }}</span>
            </div>
            <v-list density="compact" class="transparent-list">
                <v-list-item v-for="(option, oIndex) in group.options" :key="option.name" class="score-item">
                    <div class="list-item-content">
                        <div class="info" @click="showEditor(option)">
                            <span class="name">{{ option.name }}</span>
                            <span class="dots"></span>
                            <span class="value">{{ option.value }}番</span>
                        </div>
                        
                        <div v-if="isEditorOpen && selectedOption === option.name" class="editor-overlay">
                            <input
                                type="number"
                                v-model.number="option.value"
                                :max="500"
                                :min="0"
                                class="inline-input"
                                @keypress="onlyNumber"
                            />
                            <button @click="isEditorOpen = false" class="done-btn">完成</button>
                        </div>

                        <button @click="removeScore(gIndex, oIndex)" class="delete-btn">
                            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                            </svg>
                        </button>
                    </div>
                </v-list-item>
            </v-list>
        </div>

        <!-- Navigation Actions -->
        <div class="footer-reset-wrap">
            <button class="reset-all-button" @click="confirmReset">
                ⚠️ 重設此規則
            </button>
        </div>

        <!-- Danger Zone (App Only & Signed In Only) -->
        <template v-if="!isWeb && state.isSignedIn">
            <div class="danger-zone-header">
                <h3>⛔ Danger Zone</h3>
            </div>
            <div class="danger-zone">
                <button class="delete-account-link" @click="handleDeleteAccount">
                    🗑️ 刪除帳戶
                </button>
            </div>
        </template>

        <button class="back-button" @click="$router.back()">
            返回
        </button>

        <!-- Modals -->
        <div v-if="showCreateDialog" class="modal-overlay" @click.self="showCreateDialog = false">
            <div class="modal-content">
                <h3>✨ 建立新規則</h3>
                <p>將會複製「{{ state.activeProfile.name }}」的內容。</p>
                <input v-model="newProfileName" placeholder="輸入新名稱" class="modal-input" @keyup.enter="handleCreateProfile" />
                <div class="modal-actions">
                    <button @click="handleCreateProfile" class="modal-confirm-btn">建立</button>
                    <button @click="showCreateDialog = false" class="modal-cancel-btn">取消</button>
                </div>
            </div>
        </div>
    </div>
</template>
  
<script>
    import { state, saveScores, switchProfile, createProfile, deleteProfile, importProfileFromShared, resetToStandard } from '../assets/scores.js'
    import { signInWithGoogle, signInWithApple, signOut, deleteAccount, createSharedProfile, fetchSharedProfile, updateSupabaseProfile } from '../services/SupabaseService'
    import { Capacitor } from '@capacitor/core'
    import { APP_WEB_ORIGIN } from '../config.js'

    export default {
        data() {
            return {
                state,
                isWeb: Capacitor.getPlatform() === 'web',
                isAndroid: Capacitor.getPlatform() === 'android',
                isEditorOpen: false,
                selectedOption: null,
                newOptionName: '',
                newOptionValue: null,
                selectedGroupIndex: 0,
                APP_WEB_ORIGIN,
                errorMessage: '',
                showCreateDialog: false,
                newProfileName: '',
                showImportArea: false,
                importText: '',
                hasChangesSinceLoad: false,
                renamingProfileId: null,
                renamingValue: '',
                isSharingProfileId: null,
                isImporting: false,
            }
        },
        computed: {
            groups() {
                return state.groups;
            },
            sortedProfiles() {
                // Sort profiles: default first, then by updatedAt descending
                const profiles = [...state.profiles];
                return profiles.sort((a, b) => {
                    if (a.isDefault) return -1;
                    if (b.isDefault) return 1;
                    return (b.updatedAt || 0) - (a.updatedAt || 0);
                });
            },
            displayEmail() {
                if (!state.user || !state.user.email) return '已登入';
                const email = state.user.email;
                // Check if it's an Apple private relay email
                if (email.includes('.appleid.com')) {
                    return '已登入 Apple';
                }
                return `已登入: ${email}`;
            }
        },
        methods: {
            saveScores,
            handleProfileSwitch(id) {
                this.hasChangesSinceLoad = false;
                this.cancelRename();
                switchProfile(id);
            },
            startRename(profile) {
                // Don't allow renaming default profile
                if (profile.isDefault) return;
                
                this.renamingProfileId = profile.id;
                this.renamingValue = profile.name;
                this.$nextTick(() => {
                    const input = this.$refs.renameInput;
                    if (input && input[0]) input[0].focus();
                });
            },
            cancelRename() {
                this.renamingProfileId = null;
                this.renamingValue = '';
            },
            async confirmRename(profile) {
                if (!this.renamingProfileId || profile.isDefault) return;
                
                const newName = this.renamingValue.trim();
                if (newName && newName !== profile.name) {
                    profile.name = newName;
                    profile.updatedAt = Date.now();
                    if (state.isSignedIn && profile.id !== 'default') {
                        try {
                            await updateSupabaseProfile(profile.id, profile.name, profile.groups);
                        } catch (e) {
                            console.error('Failed to save profile name', e);
                        }
                    }
                }
                this.cancelRename();
            },
            handleCreateBtnClick() {
                if (!state.isSignedIn) {
                    if (confirm('登入後即可建立多個牌局規則並同步至雲端。現在去登入嗎？')) {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    return;
                }
                this.showCreateDialog = true;
            },
            handleImportBtnClick() {
                if (!state.isSignedIn) {
                    if (confirm('登入後即可匯入他人分享的規則並同步至雲端。現在去登入嗎？')) {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                    return;
                }
                this.showImportArea = !this.showImportArea;
            },
            async handleCreateProfile() {
                if (!this.newProfileName) return alert('請輸入名稱');
                await createProfile(this.newProfileName, state.groups);
                this.newProfileName = '';
                this.showCreateDialog = false;
                this.hasChangesSinceLoad = false;
            },
            async deleteProfileById(profile) {
                if (confirm(`確定要刪除「${profile.name}」嗎？`)) {
                    await deleteProfile(profile.id);
                }
            },
            async exportProfile(profile) {
                this.isSharingProfileId = profile.id;
                try {
                    const token = await createSharedProfile(profile.name, profile.groups);
                    const shareUrl = `${APP_WEB_ORIGIN}/share/${token}`;
                    
                    if (Capacitor.isNativePlatform()) {
                        const { Share } = await import('@capacitor/share');
                        await Share.share({
                            title: `麻雀規則：${profile.name}`,
                            text: `我分享了一套麻雀規則「${profile.name}」，點擊連結查看並儲存：\n${shareUrl}`,
                            url: shareUrl,
                            dialogTitle: '分享規則',
                        });
                    } else {
                        // Web fallback
                        const shareData = {
                            title: `麻雀規則：${profile.name}`,
                            text: `我分享了一套麻雀規則「${profile.name}」，點擊連結查看並儲存：\n${shareUrl}`,
                            url: shareUrl,
                        };
                        
                        if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
                            await navigator.share(shareData);
                        } else {
                            await navigator.clipboard.writeText(shareUrl);
                            alert(`分享連結已複製到剪貼簿！`);
                        }
                    }
                } catch (err) {
                    // Ignore user cancellation/abortion
                    if (err.name !== 'AbortError' && err.message !== 'Share canceled') {
                        console.error('Share failed', err);
                        alert('分享失敗，請再試一次。');
                    }
                } finally {
                    this.isSharingProfileId = null;
                }
            },
            async handleImport() {
                const input = this.importText.trim();
                if (!input) return;

                this.isImporting = true;
                try {
                    let token = input;
                    // Extract token from URL if provided
                    if (input.includes('/share/')) {
                        token = input.split('/share/').pop().split('?')[0];
                    }

                    const sharedData = await fetchSharedProfile(token);
                    
                    if (sharedData) {
                        if (await importProfileFromShared(sharedData.name, sharedData.groups)) {
                            alert('規則檔案已成功匯入！');
                            this.importText = '';
                            this.showImportArea = false;
                        } else {
                            alert('匯入失敗，請稍後再試。');
                        }
                    } else {
                        alert('連結已失效或代碼無效。');
                    }
                } catch (e) {
                    console.error('Import failed', e);
                    alert('匯入出錯，請確保連結正確。');
                } finally {
                    this.isImporting = false;
                }
            },
            async handleGoogleSignIn() {
                try {
                    await signInWithGoogle()
                } catch (e) {
                    alert('登入失敗: ' + e.message)
                }
            },
            async handleAppleSignIn() {
                try {
                    await signInWithApple()
                } catch (e) {
                    alert('登入失敗: ' + e.message)
                }
            },
            async handleSignOut() {
                if (confirm('確定要登出嗎？')) {
                    await signOut()
                }
            },
            async handleDeleteAccount() {
                const msg1 = '⚠️ 警告：這將會永久刪除您的帳戶及其所有儲存的規則。確定要繼續嗎？';
                const msg2 = '❗ 最後警告：此操作不可復原，所有規則將會消失。您真的確定要刪除帳戶嗎？';
                
                if (confirm(msg1)) {
                    if (confirm(msg2)) {
                        try {
                            await deleteAccount();
                            alert('帳戶已成功刪除。');
                            this.$router.push('/');
                        } catch (e) {
                            console.error('Failed to delete account', e);
                            alert('刪除失敗，請再試一次。');
                        }
                    }
                }
            },
            showEditor(option) {
                if (this.selectedOption === option.name) {
                    this.isEditorOpen = !this.isEditorOpen;
                } else {
                    this.selectedOption = option.name;
                    this.isEditorOpen = true;
                }
            },
            addNewScore() {
                this.errorMessage = '';
                if (!this.newOptionName || this.newOptionValue === null) {
                    this.errorMessage = '請輸入名稱及番數';
                    return;
                }

                const exists = this.groups.some(g => 
                    g.options.some(o => o.name === this.newOptionName)
                );

                if (exists) {
                    this.errorMessage = '名稱已存在';
                    return;
                }

                this.groups[this.selectedGroupIndex].options.push({
                    name: this.newOptionName,
                    value: this.newOptionValue,
                    desc: '自訂項目',
                    isSelected: false,
                    quantity: 1
                });

                this.newOptionName = '';
                this.newOptionValue = null;
                this.hasChangesSinceLoad = true;
                saveScores();
            },
            removeScore(gIndex, oIndex) {
                if (confirm('確定要刪除此番數嗎？')) {
                    this.groups[gIndex].options.splice(oIndex, 1);
                    this.hasChangesSinceLoad = true;
                    saveScores();
                }
            },
            async confirmReset() {
                const isDefault = state.activeProfile.isDefault;
                const msg = isDefault 
                    ? '⚠️ 警告：這將會恢復「標準規則」的名稱及所有番數。確定嗎？'
                    : '⚠️ 警告：這將會清除此規則中的所有自訂番數，恢復至初始狀態。確定嗎？';
                
                if (confirm(msg)) {
                    await resetToStandard();
                    this.hasChangesSinceLoad = false;
                    alert('規則已重設');
                }
            },
            formatDate(timestamp) {
                if (!timestamp) return '';
                const date = new Date(timestamp);
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, '0');
                const d = String(date.getDate()).padStart(2, '0');
                const h = String(date.getHours()).padStart(2, '0');
                const min = String(date.getMinutes()).padStart(2, '0');
                return `${y}-${m}-${d} ${h}:${min}`;
            },
            onlyNumber(evt) {
                const charCode = (evt.which) ? evt.which : evt.keyCode;
                if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                    evt.preventDefault();
                }
                return true;
            }
        },
        watch: {
            groups: {
                handler() {
                    if (this.isEditorOpen) {
                        this.hasChangesSinceLoad = true;
                    }
                },
                deep: true
            }
        }
    }
</script>

<style scoped>
    .settings-container {
        padding: 20px;
        max-width: 800px;
        margin: 0 auto 100px;
        text-align: left;
    }

    .section-box {
        background: #fff;
        border: 1px solid #eee;
        border-radius: 12px;
        padding: 20px;
        margin-bottom: 25px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.02);
    }

    .section-box h3 {
        margin: 0 0 15px 0;
        font-size: 16px;
        color: #666;
        border-bottom: 1px solid #f5f5f5;
        padding-bottom: 10px;
    }

    .beta-tag {
        display: inline-block;
        margin-left: 6px;
        padding: 1px 7px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
        vertical-align: middle;
        color: #8a6d1d;
        background: #f5efd8;
        border: 1px solid #e6d9a8;
        border-radius: 4px;
    }

    /* User Section Styles */
    .user-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .user-email {
        font-weight: 600;
        color: #333;
        margin: 0;
    }

    .sync-status {
        font-size: 12px;
        color: #4CAF50;
        margin: 4px 0 0 0;
    }

    .sign-out-btn {
        background: #fff1f0;
        color: #cf1322;
        border: 1px solid #ffa39e;
        padding: 6px 12px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 13px;
    }

    .auth-options p {
        font-size: 14px;
        color: #666;
        margin-bottom: 15px;
    }

    .auth-btns {
        display: flex;
        gap: 10px;
    }

    .google-btn, .apple-btn {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 10px;
        border-radius: 8px;
        border: 1px solid #ddd;
        background: white;
        cursor: pointer;
        font-weight: 500;
        transition: background 0.2s;
    }

    .google-btn:hover { background: #f8f9fa; }
    .apple-btn { background: #000; color: #fff; border: none; }
    .apple-btn:hover { background: #333; }

    .login-nudge {
        font-size: 12px;
        color: #888;
        margin-top: 10px;
        text-align: center;
    }

    /* Profile Section Styles */
    .profile-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 20px;
    }

    .profile-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 15px;
        background: #fcfcfc;
        border: 1px solid #eee;
        border-radius: 10px;
        cursor: pointer;
        transition: all 0.2s ease;
    }

    .profile-item:hover {
        background: #f5f5f5;
        border-color: #ddd;
    }

    .profile-item.active {
        background: #e8f5e9;
        border-color: #4CAF50;
        box-shadow: 0 2px 8px rgba(76, 175, 80, 0.1);
    }

    .profile-info {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .name-date-stack {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .profile-date-text {
        font-size: 11px;
        color: #999;
    }

    .active-indicator {
        color: #4CAF50;
        font-weight: bold;
        font-size: 16px;
    }

    .profile-name-text {
        font-weight: 500;
        color: #333;
    }

    .profile-item.active .profile-name-text {
        color: #2e7d32;
        font-weight: 600;
    }

    .profile-item-actions {
        display: flex;
        gap: 8px;
    }

    .inline-rename-wrap {
        display: flex;
        align-items: center;
        background: white;
        padding: 0 5px;
        border-radius: 6px;
    }

    .mini-rename-input {
        border: 1px solid #4CAF50;
        border-radius: 4px;
        padding: 4px 8px;
        font-size: 13px;
        width: 120px;
        outline: none;
    }

    .mini-btn {
        background: white;
        border: 1px solid #e0e0e0;
        padding: 10px 12px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #666;
    }

    .mini-btn:hover {
        background: #f0f0f0;
    }

    .mini-btn.edit:hover {
        background: #e3f2fd;
        color: #1976d2;
        border-color: #90caf9;
    }

    .mini-btn.share:hover {
        background: #f3e5f5;
        color: #7b1fa2;
        border-color: #ce93d8;
    }

    .mini-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .share-spinner {
        width: 14px;
        height: 14px;
        border: 2px solid #ce93d8;
        border-top-color: #7b1fa2;
        border-radius: 50%;
        animation: share-spin 0.7s linear infinite;
    }

    @keyframes share-spin {
        to { transform: rotate(360deg); }
    }

    .mini-btn.delete {
        color: #cf1322;
        border-color: #ffa39e;
    }

    .mini-btn.delete:hover {
        background: #fff1f0;
    }

    .profile-lower-row {
        border-top: 1px solid #f5f5f5;
        padding-top: 15px;
    }

    .profile-rename-input {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
        margin-bottom: 12px;
        box-sizing: border-box;
    }

    .footer-btns {
        display: flex;
        justify-content: space-between;
        gap: 10px;
    }

    .create-new-btn {
        flex: 1;
        background: #4CAF50;
        color: white;
        border: none;
        padding: 10px;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        font-size: 13px;
    }

    .import-toggle-btn {
        flex: 1;
        background: #e3f2fd;
        color: #1976d2;
        border: 1px solid #bbdefb;
        padding: 10px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 500;
        transition: all 0.2s;
    }

    .import-toggle-btn:hover {
        background: #bbdefb;
        border-color: #90caf9;
    }

    /* Import Sub-section */
    .import-sub-section {
        margin-top: 20px;
        background: #fdfdfd;
        border: 1px solid #eee;
        padding: 15px;
        border-radius: 10px;
    }

    .import-hint {
        margin: 0 0 10px;
        font-size: 13px;
        color: #666;
    }

    .import-textarea {
        width: 100%;
        height: 80px;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-family: monospace;
        font-size: 12px;
        margin-bottom: 10px;
        box-sizing: border-box;
    }

    .import-btns {
        display: flex;
        gap: 12px;
        margin-top: 5px;
    }

    .confirm-import-btn {
        background: #4CAF50;
        color: white;
        border: none;
        padding: 8px 20px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        transition: all 0.2s;
        box-shadow: 0 2px 6px rgba(76, 175, 80, 0.2);
    }

    .confirm-import-btn:hover {
        background: #43a047;
        transform: translateY(-1px);
        box-shadow: 0 4px 10px rgba(76, 175, 80, 0.3);
    }

    .cancel-btn-small {
        background: #f5f5f5;
        color: #666;
        border: 1px solid #ddd;
        padding: 8px 20px;
        border-radius: 8px;
        cursor: pointer;
        font-size: 13px;
        transition: all 0.2s;
    }

    .cancel-btn-small:hover {
        background: #eeeeee;
    }

    /* Nudge Style */
    .default-nudge {
        margin-top: 15px;
        padding: 10px;
        background: #fffbe6;
        border: 1px solid #ffe58f;
        border-radius: 6px;
        font-size: 13px;
        color: #856404;
    }

    .nudge-link {
        background: none;
        border: none;
        color: #1976d2;
        text-decoration: underline;
        cursor: pointer;
        padding: 0;
        font-size: 13px;
        margin-left: 5px;
    }

    /* Add Score Form Styles (Original Part) */
    .form-row {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
    }

    .input-name {
        flex: 2;
        min-width: 150px;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 8px;
    }

    .input-value {
        flex: 0.5;
        min-width: 60px;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 8px;
    }

    .select-group {
        flex: 1;
        min-width: 120px;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 8px;
        background: white;
    }

    .add-btn {
        background: #4CAF50;
        color: white;
        border: none;
        padding: 10px 25px;
        border-radius: 8px;
        cursor: pointer;
        font-weight: bold;
    }

    .error-msg {
        color: #e74c3c;
        font-size: 14px;
        margin-top: 10px;
    }

    /* Score List Styles (Section 3) */
    .group-section {
        margin-bottom: 30px;
    }

    .group-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border-bottom: 2px solid #4CAF50;
        padding-bottom: 8px;
        margin-bottom: 15px;
    }

    .group-header h2 {
        margin: 0;
        font-size: 18px;
    }

    .count-tag {
        background: #f0f0f0;
        color: #999;
        font-size: 12px;
        padding: 2px 8px;
        border-radius: 10px;
    }

    .list-item-content {
        display: flex;
        align-items: center;
        width: 100%;
        gap: 10px;
        padding: 5px 0;
    }

    .info {
        display: flex;
        align-items: center;
        flex: 1;
        cursor: pointer;
        padding: 5px;
        border-radius: 5px;
    }

    .info:hover {
        background: #f5f5f5;
    }

    .name {
        font-weight: 500;
        color: #333;
    }

    .dots {
        flex: 1;
        border-bottom: 1px dotted #ccc;
        margin: 0 10px;
        height: 14px;
    }

    .value {
        color: #4CAF50;
        font-weight: bold;
    }

    .editor-overlay {
        display: flex;
        gap: 5px;
        background: white;
        padding: 2px;
        border-radius: 5px;
    }

    .inline-input {
        width: 60px;
        padding: 5px;
        border: 1px solid #4CAF50;
        border-radius: 4px;
    }

    .done-btn {
        background: #4CAF50;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 4px;
        font-size: 12px;
    }

    .delete-btn {
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 5px;
        color: #666;
        opacity: 0.5;
        transition: all 0.2s;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .delete-btn:hover {
        opacity: 1;
        color: #cf1322;
    }

    /* Footer Reset Wrapper */
    .footer-reset-wrap {
        margin-top: 60px;
        text-align: center;
        padding-bottom: 20px;
    }

    .reset-all-button {
        background-color: #f5f5f5;
        color: #666;
        border: 1px solid #ddd;
        padding: 10px 25px;
        border-radius: 20px;
        cursor: pointer;
        font-weight: 500;
        font-size: 13px;
        transition: all 0.2s;
    }

    .reset-all-button:hover {
        background-color: #eeeeee;
        border-color: #ccc;
    }

    /* Danger Zone */
    .danger-zone {
        margin-top: 5px;
        padding: 15px;
        background: #fffafa;
        border: 1px solid #fee;
        border-radius: 12px;
        text-align: center;
        margin-bottom: 20px;
    }

    .danger-zone-header {
        margin-top: 20px;
        padding: 0 10px;
    }

    .danger-zone-header h3 {
        color: #cf1322;
        font-size: 15px;
        margin-bottom: 8px;
        font-weight: 600;
    }

    .delete-account-link {
        background: none;
        border: none;
        color: #999;
        font-size: 13px;
        text-decoration: underline;
        cursor: pointer;
        transition: color 0.2s;
        padding: 5px 10px;
    }

    .delete-account-link:hover {
        color: #cf1322;
    }

    .back-button {
        position: fixed;
        bottom: calc(30px + env(safe-area-inset-bottom));
        left: 50%;
        transform: translateX(-50%);
        padding: 12px 35px;
        background-color: #666;
        color: white;
        border: none;
        border-radius: 30px;
        cursor: pointer;
        font-weight: bold;
        box-shadow: 0 4px 15px rgba(0,0,0,0.2);
        z-index: 100;
    }
    /* Modal Styles */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2000;
    }

    .modal-content {
        background: white;
        padding: 25px;
        border-radius: 16px;
        width: 90%;
        max-width: 350px;
    }

    .modal-input {
        width: 100%;
        padding: 10px;
        margin: 15px 0;
        border: 1px solid #ddd;
        border-radius: 8px;
        box-sizing: border-box;
    }

    .modal-actions {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    .modal-confirm-btn {
        background: #4CAF50;
        color: white;
        border: none;
        padding: 8px 15px;
        border-radius: 6px;
        cursor: pointer;
    }

    .transparent-list {
        background: transparent !important;
    }
</style>